import db from "../db/database";
import { normalizarTexto } from "./unicidad";
import { removeAccents } from "../utils/stringNormalization";
import { resolverTipoDeDocumento } from "../constants/identityDocuments";

/**
 * Cuándo dos filas de cliente son la misma persona.
 *
 * Vive aparte porque la pregunta se hace en dos sitios que no deben depender uno
 * del otro: el **alta**, que rechaza un duplicado, y el **pull**, que empareja lo
 * que llega de Business Central con lo que ya hay en local. Tenerla escrita dos
 * veces era peor que tenerla suelta: el alta se aflojó para que un «No
 * contribuyente» de Costa Rica pudiera darse de alta más de una vez, y el pull
 * se quedó con el criterio viejo -- que además no rechaza nada, **sobrescribe**.
 */

/**
 * Cómo se comparan el nombre y el documento de un cliente.
 *
 * Mayúsculas, sin espacios de sobra y **sin acentos**. Lo último hace falta desde
 * que el documento dejó de comprobarse cuando es de relleno: para un no
 * contribuyente el nombre pasó a ser lo único que lo distingue, y sin esto
 * «JOSÉ PEREZ» y «JOSE PEREZ» entraban los dos.
 */
export const normalizarDatoDeCliente = (valor) => removeAccents(normalizarTexto(valor));

/**
 * ¿Este documento identifica a alguien?
 *
 * Un «No contribuyente» de Costa Rica no tiene documento: se le pone un relleno,
 * y en la práctica todos llevan `0`. Como la comprobación de duplicados sólo
 * miraba el número, el primer no contribuyente se quedaba con el cero y **el
 * segundo ya no se podía dar de alta**: el terminal decía que ya existía un
 * cliente con ese documento y nombraba a otra persona.
 *
 * Un documento que es todo ceros --o guiones, que es el otro relleno habitual--
 * no distingue a nadie, así que se trata igual que el vacío.
 *
 * Reconoce sólo estos dos rellenos a propósito. Hay otros imaginables --`N/A`,
 * `CF`, una fila de nueves-- y no se añaden a ciegas: cada uno que se acepte
 * afloja la identidad, y conviene medir contra el ambiente cuáles se usan de
 * verdad antes de darlos por relleno.
 */
export const documentoIdentifica = (valor) => {
  const limpio = normalizarDatoDeCliente(valor).replace(/[\s.-]/g, "");

  return limpio !== "" && !/^0+$/.test(limpio);
};

/**
 * El tipo de documento de una fila, en la grafía canónica del catálogo.
 *
 * Pasa por `resolverTipoDeDocumento` y no por el texto crudo porque Business
 * Central usa sus propios alias: en Ecuador devuelve `ID`, `VAT` y `Passport`
 * donde el formulario guarda `Cedula`, `RUC` y `Pasaporte`. Comparando el texto
 * tal cual, la misma persona con la misma cédula parecía tener dos tipos
 * distintos y **el duplicado entraba**.
 *
 * Sin país no se puede resolver el alias, y entonces se devuelve lo que haya:
 * comparar de más es preferible a comparar mal.
 *
 * El respaldo a `Tax_Identification_Type` es para ese caso sin país. Con país,
 * `resolverTipoDeDocumento` ya sabe que en Costa Rica el tipo viaja con ese
 * nombre; sin él supone el nombre por defecto, y una fila cruda del pull
 * costarricense se quedaría sin tipo.
 */
export const tipoDeDocumentoDe = (cliente, country) =>
  normalizarDatoDeCliente(
    resolverTipoDeDocumento(cliente, country) || cliente?.Tax_Identification_Type
  );

/**
 * ¿Estos dos documentos viven en el mismo espacio de identidad?
 *
 * Una cédula física `123456789` y un «No contribuyente` con el mismo relleno no
 * son la misma persona: son dos numeraciones distintas que coinciden en los
 * dígitos. Comparar sólo el número los daba por el mismo cliente.
 *
 * Cuando a alguno de los dos le falta el tipo --filas viejas, o traídas de BC
 * sin normalizar-- no se puede decidir, y ahí se compara sólo el número, como
 * antes: dejar pasar un duplicado de verdad es peor que señalar uno de más.
 */
export const mismoEspacioDeIdentidad = (uno, otro, country) => {
  const tipoDeUno = tipoDeDocumentoDe(uno, country);
  const tipoDelOtro = tipoDeDocumentoDe(otro, country);

  if (!tipoDeUno || !tipoDelOtro) return true;

  return tipoDeUno === tipoDelOtro;
};

/**
 * El país del terminal, para resolver los alias del tipo de documento.
 *
 * No usa `getActiveEnvironment` porque aquélla lanza cuando no hay ambiente, y
 * acá la ausencia no es un error: sin país se compara por el texto crudo, que es
 * el lado conservador.
 */
export const paisDelTerminal = async () => {
  try {
    const ambiente = await db.environment.filter((e) => e.isActive === true).first();

    return ambiente?.country;
  } catch {
    return undefined;
  }
};
